import React, { useState, useEffect } from 'react';
import {
  Flex,
  Typography,
  Button,
  Table,
  Card,
  Tabs,
  Modal,
  Form,
  Input,
  Checkbox,
  Tag,
  Tooltip,
  Spin,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import dashboardService from '../../service';
import { useNotification } from '../../hooks/useNotification';
import { useTranslation } from '../../i18n/TranslationContext';

const AuctionFeesPage = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [fees, setFees] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('armenia');
  const [selectedAuction, setSelectedAuction] = useState('copart');
  const [countryTab, setCountryTab] = useState('armenia');
  const [auctionTab, setAuctionTab] = useState('copart');

  const [formOpen, setFormOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editingFee, setEditingFee] = useState(null);
  const [selectedFee, setSelectedFee] = useState(null);

  const fetchFees = async () => {
    setLoading(true);
    try {
      const response = await dashboardService.getAuctionFees({
        country: selectedCountry,
        auction: selectedAuction,
        limit: 1000,
      });

      if (response.success) {
        setFees(response.data || []);
      } else {
        showError(response.error || 'Failed to fetch fees');
      }
    } catch (error) {
      showError('Error fetching auction fees');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await dashboardService.getAuctionFeeStatistics();
      if (response.success) {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  useEffect(() => {
    fetchFees();
    fetchStatistics();
  }, [selectedCountry, selectedAuction]);

  const handleCountryTabChange = (key) => {
    setCountryTab(key);
    setSelectedCountry(key);
  };

  const handleAuctionTabChange = (key) => {
    setAuctionTab(key);
    setSelectedAuction(key);
  };

  const handleAddNew = () => {
    setEditingFee(null);
    form.resetFields();
    form.setFieldsValue({ is_percent: false });
    setFormOpen(true);
  };

  const handleEdit = (fee) => {
    setEditingFee(fee);
    form.setFieldsValue({
      price_from: fee.price_from ?? '',
      price_to: fee.price_to ?? '',
      bidding_fee: fee.bidding_fee ?? '',
      gate_fee: fee.gate_fee ?? '',
      virtual_bid_fee: fee.virtual_bid_fee ?? '',
      broker_fee: fee.broker_fee ?? '',
      percent: fee.percent ?? '',
      is_percent: fee.is_percent ?? false,
    });
    setFormOpen(true);
  };

  const handleDeleteClick = (fee) => {
    setSelectedFee(fee);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await dashboardService.deleteAuctionFee(selectedFee.id);
      if (response.success) {
        showSuccess('Fee deleted successfully');
        fetchFees();
        fetchStatistics();
      } else {
        showError(response.error || 'Failed to delete fee');
      }
    } catch (error) {
      showError('Error deleting fee');
      console.error('Error:', error);
    } finally {
      setDeleteModalOpen(false);
      setSelectedFee(null);
    }
  };

  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      const feeData = {
        ...values,
        country: selectedCountry,
        auction: selectedAuction,
        price_from: parseFloat(values.price_from) || 0,
        price_to: parseFloat(values.price_to) || 0,
        bidding_fee: parseFloat(values.bidding_fee) || 0,
        gate_fee: parseFloat(values.gate_fee) || 0,
        virtual_bid_fee: parseFloat(values.virtual_bid_fee) || 0,
        broker_fee: parseFloat(values.broker_fee) || 0,
        percent: parseFloat(values.percent) || 0,
      };

      let response;
      if (editingFee) {
        response = await dashboardService.updateAuctionFee(editingFee.id, feeData);
      } else {
        response = await dashboardService.createAuctionFee(feeData);
      }

      if (response.success) {
        showSuccess(editingFee ? 'Fee updated successfully' : 'Fee created successfully');
        setFormOpen(false);
        fetchFees();
        fetchStatistics();
      } else {
        showError(response.error || 'Failed to save fee');
      }
    } catch (error) {
      if (error.errorFields) return;
      showError('Error saving fee');
      console.error('Error:', error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    {
      title: 'Price From',
      dataIndex: 'price_from',
      key: 'price_from',
      render: (v) => formatCurrency(v),
    },
    {
      title: 'Price To',
      dataIndex: 'price_to',
      key: 'price_to',
      render: (v) => (v === 0 ? '∞' : formatCurrency(v)),
    },
    {
      title: 'Bidding Fee',
      dataIndex: 'bidding_fee',
      key: 'bidding_fee',
      render: (v) => formatCurrency(v),
    },
    {
      title: 'Gate Fee',
      dataIndex: 'gate_fee',
      key: 'gate_fee',
      render: (v) => formatCurrency(v),
    },
    {
      title: 'Virtual Bid',
      dataIndex: 'virtual_bid_fee',
      key: 'virtual_bid_fee',
      render: (v) => formatCurrency(v),
    },
    {
      title: 'Broker Fee',
      dataIndex: 'broker_fee',
      key: 'broker_fee',
      render: (v) => formatCurrency(v),
    },
    {
      title: 'Percent',
      dataIndex: 'percent',
      key: 'percent',
      render: (v) => (v != null ? `${Number(v).toFixed(2)}%` : '-'),
    },
    {
      title: 'Type',
      dataIndex: 'is_percent',
      key: 'is_percent',
      render: (v) => (
        <Tag color={v ? 'success' : 'default'}>{v ? 'Percent' : 'Fixed'}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      render: (_, record) => (
        <Flex gap={8} justify="center">
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteClick(record)}
            />
          </Tooltip>
        </Flex>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 24 }}>
        <Flex vertical>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {t('nav.auctionFees') || 'Auction Fees'}
          </Typography.Title>
          <Typography.Text type="secondary">
            Manage auction fees for Armenia and Georgia
          </Typography.Text>
        </Flex>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNew} size="large">
          Add New Fee
        </Button>
      </Flex>

      {statistics && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Flex vertical align="center" gap={8}>
                <Typography.Text type="secondary" style={{ textTransform: 'uppercase', fontWeight: 600 }}>
                  Total Fees
                </Typography.Text>
                <Typography.Title level={2} style={{ margin: 0 }}>
                  {statistics.total}
                </Typography.Title>
                <Typography.Text type="secondary">All Countries</Typography.Text>
              </Flex>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ background: 'linear-gradient(135deg, #d32f2f 0%, #e57373 100%)', color: 'white' }}>
              <Flex vertical align="center" gap={8}>
                <Typography.Text style={{ opacity: 0.9, textTransform: 'uppercase', fontWeight: 600 }}>
                  Armenia
                </Typography.Text>
                <Typography.Title level={2} style={{ margin: 0, color: 'white' }}>
                  {statistics.armenia?.total || 0}
                </Typography.Title>
                <Typography.Text style={{ opacity: 0.8 }}>
                  🇦🇲 {statistics.armenia?.copart?.total || 0} Copart · {statistics.armenia?.iaai?.total || 0} IAAI
                </Typography.Text>
              </Flex>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ background: 'linear-gradient(135deg, #f57c00 0%, #ffb74d 100%)', color: 'white' }}>
              <Flex vertical align="center" gap={8}>
                <Typography.Text style={{ opacity: 0.9, textTransform: 'uppercase', fontWeight: 600 }}>
                  Georgia
                </Typography.Text>
                <Typography.Title level={2} style={{ margin: 0, color: 'white' }}>
                  {statistics.georgia?.total || 0}
                </Typography.Title>
                <Typography.Text style={{ opacity: 0.8 }}>
                  🇬🇪 {statistics.georgia?.copart?.total || 0} Copart · {statistics.georgia?.iaai?.total || 0} IAAI
                </Typography.Text>
              </Flex>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%)', color: 'white' }}>
              <Flex vertical align="center" gap={8}>
                <Typography.Text style={{ opacity: 0.9, textTransform: 'uppercase', fontWeight: 600 }}>
                  Current View
                </Typography.Text>
                <Typography.Title level={2} style={{ margin: 0, color: 'white' }}>
                  {fees.length}
                </Typography.Title>
                <Flex gap={8}>
                  <Tag style={{ background: 'rgba(255,255,255,0.25)', color: 'white' }}>
                    {selectedCountry === 'armenia' ? '🇦🇲 Armenia' : '🇬🇪 Georgia'}
                  </Tag>
                  <Tag style={{ background: 'rgba(255,255,255,0.25)', color: 'white' }}>
                    {selectedAuction.toUpperCase()}
                  </Tag>
                </Flex>
              </Flex>
            </Card>
          </Col>
        </Row>
      )}

      <Card style={{ marginBottom: 16 }}>
        <Tabs
          activeKey={countryTab}
          onChange={handleCountryTabChange}
          items={[
            { key: 'armenia', label: '🇦🇲 Armenia' },
            { key: 'georgia', label: '🇬🇪 Georgia' },
          ]}
        />
      </Card>

      <Card style={{ marginBottom: 24 }}>
        <Tabs
          activeKey={auctionTab}
          onChange={handleAuctionTabChange}
          items={[
            { key: 'copart', label: 'Copart' },
            { key: 'iaai', label: 'IAAI' },
          ]}
        />
      </Card>

      <Card>
        {loading ? (
          <Flex justify="center" align="center" style={{ padding: 48 }}>
            <Spin size="large" />
          </Flex>
        ) : (
          <Table
            columns={columns}
            dataSource={fees.map((f) => ({ ...f, key: f.id }))}
            locale={{
              emptyText: `No fees found for ${selectedCountry.toUpperCase()} - ${selectedAuction.toUpperCase()}`,
            }}
          />
        )}
      </Card>

      <Modal
        title={
          <Flex vertical>
            <span>{editingFee ? 'Edit Auction Fee' : 'Add New Auction Fee'}</span>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {selectedCountry.toUpperCase()} - {selectedAuction.toUpperCase()}
            </Typography.Text>
          </Flex>
        }
        open={formOpen}
        onCancel={() => setFormOpen(false)}
        onOk={handleFormSubmit}
        okText={editingFee ? 'Update' : 'Create'}
        width={600}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="price_from" label="Price From *" rules={[{ required: true }]}>
                <Input type="number" step={0.01} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="price_to" label="Price To * (0 = unlimited)" rules={[{ required: true }]}>
                <Input type="number" step={0.01} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="bidding_fee" label="Bidding Fee">
                <Input type="number" step={0.01} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="gate_fee" label="Gate Fee">
                <Input type="number" step={0.01} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="virtual_bid_fee" label="Virtual Bid Fee">
                <Input type="number" step={0.01} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="broker_fee" label="Broker Fee">
                <Input type="number" step={0.01} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="percent" label="Percent (%)">
                <Input type="number" step={0.01} min={0} max={100} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="is_percent" valuePropName="checked">
                <Checkbox>Is Percent Based</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Confirm Delete"
        open={deleteModalOpen}
        onCancel={() => setDeleteModalOpen(false)}
        onOk={handleDeleteConfirm}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <Typography.Text>
          Are you sure you want to delete fee ID {selectedFee?.id}?
        </Typography.Text>
      </Modal>
    </div>
  );
};

export default AuctionFeesPage;
